import React, { memo, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { TextField, Autocomplete, ListItem } from "investira.react.components";
import { validators } from "investira.sdk";
import withResponseHandling from "../../../hoc/withResponseHandling";
import utils from "../../../utils";

/**
 * Renderiza um autocomplete assíncrono com busca remota e estado local de opções.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {Function} props.service - Serviço responsável por buscar as opções.
 * @param {string} props.id - Identificador usado no campo renderizado.
 * @param {string} props.renderOptionKey - Chave usada para exibir o texto padrão de cada opção.
 * @param {string} props.label - Texto exibido como rótulo do campo.
 * @param {string} props.name - Nome usado pelo autocomplete.
 * @param {Object} props.sx - Estilos adicionais repassados via `sx`.
 * @param {*} props.serviceParams - Parâmetros extras enviados ao serviço de busca.
 * @param {Array} props.initOptions - Lista inicial de opções exibidas no autocomplete.
 * @param {Object|string|number|null} props.initValue - Valor inicial selecionado.
 * @param {string} props.initInputValue - Texto inicial exibido no input.
 * @param {string} props.placeholder - Placeholder exibido no campo.
 * @param {Function} props.onChange - Callback executado ao selecionar uma opção.
 * @param {*} props.refreshInput - Dependência usada para resetar o campo externamente.
 * @param {Function} props.renderOption - Renderização customizada de cada opção.
 * @returns {JSX.Element} Elemento renderizado pelo componente.
 */
const InputAsync = memo((props) => {
  const {
    service,
    serviceParams,
    id,
    sx,
    label = "label",
    renderOptionKey,
    initOptions,
    initValue,
    initInputValue,
    placeholder,
    onChange,
    refreshInput,
    // name,
    // getOptionLabel,
    // renderOption,
  } = props;

  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState(null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noOptionsText, setNoOptionsText] = useState(
    "Aguardando a busca do usuário"
  );

  // console.log("inputValue", inputValue);
  // console.log("options", options);
  // console.log("value", value);
  // console.log("loading", loading);
  // console.log("noOptionsText", noOptionsText);

  const request = useMemo(() => {
    return utils.helpers.debounce((pParams, pRes, pErr, pFin) => {
      setLoading(true);
      service(pParams, pRes, pErr, pFin);
    }, 400);
  }, []);

  function handleChange(pEvent, pNewValue) {
    setOptions(pNewValue ? [pNewValue, ...options] : options);
    setValue(pNewValue);
    onChange && onChange(pEvent, pNewValue);
  }

  function verifySpecialChar(pStr) {
    const regex = /[./-]/;
    return regex.test(pStr);
  }

  function cleanSpecialChar(pStr) {
    const regex = /[./-]/g;
    if (verifySpecialChar(pStr)) {
      return pStr.replace(regex, "");
    }

    return pStr;
  }

  function formatSearch(pValue) {
    let xValue = cleanSpecialChar(pValue);

    let xStrings = xValue.split(" ");
    xStrings = xStrings.map((xString) => {
      return `+${xString}*`.trim();
    });

    const xSearchFormated = xStrings.join("");

    return xSearchFormated;
  }

  useEffect(() => {
    if (!validators.isEmpty(initOptions)) {
      setOptions(initOptions);
    }
  }, [initOptions]);

  useEffect(() => {
    if (!validators.isEmpty(initValue)) {
      setValue(initValue);
    }
  }, [initOptions, initValue, initInputValue]);

  useEffect(() => {
    if (!validators.isEmpty(initInputValue)) {
      setInputValue(initInputValue);
    }
  }, [initInputValue]);

  // useEffect(() => {
  //   if (
  //     typeof initInputValue === "string" &&
  //     initInputValue.trim() === "" &&
  //     validators.isEmpty(options)
  //   ) {
  //     setLoading(false);
  //     setNoOptionsText("Aguardando a busca do usuário");
  //   }
  // }, [initInputValue, options]);

  useEffect(() => {
    setValue(null);
    setInputValue("");
    setOptions([]);
  }, [refreshInput]);

  useEffect(() => {
    let active = true;

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    if (open) {
      const xPesquisa = formatSearch(inputValue);

      request(
        { size: 25, pesquisa: xPesquisa, ...serviceParams },
        (rRes) => {
          if (active) {
            //setNoOptionsText("Carregando ativos...");
            let xNewOptions = [];

            if (value) {
              xNewOptions = [value];
            }

            if (!validators.isEmpty(rRes.data)) {
              const xData = Object.values(rRes.data);
              xNewOptions = [...xNewOptions, ...xData];
            } else {
              setNoOptionsText("Nenhum ativo encontrado.");
            }

            setOptions(xNewOptions);
          }
        },
        (rErr) => {
          props.responseErrorHandling(rErr, {
            err400: props.messageError,
            //err404: () => setNoOptionsText("Reiniciando a busca..."),
          });
        },
        () => {
          setLoading(false);
        }
      );
    }

    return () => {
      active = false;
    };
  }, [request, inputValue, value]);

  return (
    <Autocomplete
      id={`async-controlled-${id}`}
      sx={{ ...sx }}
      getOptionLabel={(option) => {
        return typeof option === "string" ? option : option[renderOptionKey];
      }}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      loading={loading}
      loadingText="Buscando..."
      filterSelectedOptions
      inputValue={inputValue}
      value={value}
      noOptionsText={noOptionsText} //"Nenhum dado encontrado"
      onChange={handleChange}
      onInputChange={(_pEvent, pNewInputValue) => {
        setInputValue(pNewInputValue);
        setOptions([]);
        setLoading(true);
        if (!pNewInputValue) {
          setLoading(false);
          setNoOptionsText("Aguardando a busca do usuário");
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          placeholder={placeholder}
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{
            ...params.inputProps,
            autoComplete: "new-password",
          }}
          // InputProps={{
          //   endAdornment: (
          //     <>
          //       {loading ? (
          //         <CircularProgress color="inherit" size={20} />
          //       ) : null}
          //       {params.InputProps.endAdornment}
          //     </>
          //   ),
          // }}
        />
      )}
      renderOption={(pProps, pOption, pState, pOwnerState) => {
        if (!validators.isNull(props.renderOption)) {
          return props.renderOption(pProps, pOption, pState, pOwnerState);
        }

        return (
          <ListItem component="li" {...pProps}>
            {pOption[renderOptionKey]}
          </ListItem>
        );
      }}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
    />
  );
});

InputAsync.propTypes = {
  service: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  renderOptionKey: PropTypes.string.isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  sx: PropTypes.object,
};

InputAsync.displayName = "InputAsync";

export default withResponseHandling(InputAsync);
